import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail, CommonUtils } from '../../utils/commonUtils';
import {
  createVoucher,
  createVoucherFile,
  toVoucherPrimaryFileRequest,
} from '../../utils/voucher';
import { FormData, FormErrors, VoucherFormUtils } from '../../utils/voucherCommon';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';
import {
  UploadFilePayload,
  toUploadFilePayload,
  uploadSingleFileViaPresign,
} from '../../utils/storage';
import { datetimeLocalToIsoUtc } from '../../utils/dateTime';
import VoucherPreviewCard, {
  formatVoucherPreviewBenefit,
  formatVoucherPreviewValidity,
} from './components/VoucherPreviewCard';
import { 
  Save,
  ArrowLeft,
  X,
  Check,
  Info,
  DollarSign,
  Gift,
  Settings,
  FileText,
  RefreshCw,
  Calendar,
  Percent,
} from 'lucide-react';

const VoucherCreate: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('voucher-list');
  
  // Form state
  const [formData, setFormData] = useState<FormData>(VoucherFormUtils.getDefaultFormData());

  // Form errors
  const [errors, setErrors] = useState<FormErrors>({});

  // API state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Brand state
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);

  // 대표 이미지
  const [uploadFiles, setUploadFiles] = useState<UploadFilePayload[]>([]);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string>('');

  // Check if screen is mobile size
  VoucherFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = VoucherFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 브랜드 목록 조회
  useEffect(() => {
    if (!projectDetail) return;
    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode) return;
    let cancelled = false;
    setIsLoadingBrands(true);
    getBrandListFlat(projectCode)
      .then((res) => {
        if (!cancelled && res?.brands) setBrands(res.brands);
        else if (!cancelled) setBrands([]);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('브랜드 목록 조회 오류:', err);
          setBrands([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingBrands(false);
      });
    return () => { cancelled = true; };
  }, [projectDetail]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    VoucherFormUtils.handleInputChange(e, setFormData, setErrors);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors = VoucherFormUtils.validateCreateForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reset form
  const handleReset = () => {
    setFormData(VoucherFormUtils.getDefaultFormData());
    setErrors({});
    setSubmitError(null);
    setUploadFiles([]);
    setImagePreview('');
    setImageError('');
  };

  const handleValidatedImage = async ({ file, dataUrl }: { file: File; dataUrl: string }) => {
    const projectCode = projectDetail?.code || getProjectCode();
    if (!projectCode) {
      setImageError('프로젝트 정보가 없습니다.');
      return;
    }

    setUploadFiles([]);
    setImagePreview('');
    setImageError('');
    setIsImageUploading(true);

    try {
      const { upload } = await uploadSingleFileViaPresign(file, {
        domain: 'VOUCHER',
        projectCode,
      });
      setUploadFiles([toUploadFilePayload(upload)]);
      setImagePreview(dataUrl);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : '이미지 업로드 중 오류가 발생했습니다';
      setImageError(message);
      setUploadFiles([]);
      setImagePreview('');
    } finally {
      setIsImageUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setUploadFiles([]);
    setImagePreview('');
    setImageError('');
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isImageUploading) {
      alert('이미지 업로드가 진행 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const projectCode = getProjectCode();
      if (!projectCode) {
        throw new Error('프로젝트 정보를 찾을 수 없습니다.');
      }

      const requestData = {
        brandCode: formData.brandCode?.trim() || undefined,
        productCode: formData.productCode.trim() || null,
        masterCode: formData.masterCode,
        status: formData.status,
        type: formData.type,
        name: formData.name,
        price: formData.price,
        balance: formData.balance,
        discountTarget: formData.discountTarget,
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        maxDiscount: formData.maxDiscount || undefined,
        minPayment: formData.minPayment || undefined,
        validType: formData.validType,
        validPeriod: formData.validType === 0 ? formData.validPeriod || undefined : undefined,
        validUntil:
          formData.validType === 1 && String(formData.validUntil || '').trim()
            ? datetimeLocalToIsoUtc(String(formData.validUntil).trim())
            : undefined,
        issueLimit: formData.issueLimit,
        memberLimit: formData.memberLimit || undefined,
        comment: formData.comment || undefined,
        description: formData.description || undefined,
        notice: formData.notice || undefined,
      };

      const response = await createVoucher(projectCode, requestData);
      if (!response.success) {
        throw new Error(response.message || '교환권 생성에 실패했습니다.');
      }

      const createdCode =
        response.data?.voucherCode ||
        (response.data as { code?: string } | undefined)?.code;

      if (uploadFiles.length > 0) {
        if (!createdCode) {
          alert(
            '교환권은 생성되었지만 코드가 없어 이미지를 등록하지 못했습니다. 수정 화면에서 다시 업로드해 주세요.'
          );
          navigate('/vouchers');
          return;
        }

        try {
          for (const uploadFile of uploadFiles) {
            await createVoucherFile(
              projectCode,
              createdCode,
              toVoucherPrimaryFileRequest(uploadFile)
            );
          }
        } catch (fileError) {
          console.error('교환권 이미지 등록 실패:', fileError);
          alert(
            '교환권은 생성되었습니다.\n이미지 등록에 실패했습니다. 수정 화면에서 다시 업로드해 주세요.'
          );
          navigate('/vouchers');
          return;
        }
      }

      alert('교환권이 성공적으로 생성되었습니다.');
      navigate('/vouchers');
    } catch (error: unknown) {
      console.error('교환권 생성 오류:', error);
      const displayMessage =
        error instanceof Error
          ? error.message
          : CommonUtils.getNetworkErrorMessage(error) || '교환권 생성 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 프로젝트 정보가 없으면 로딩 표시
  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar
          title="교환권 생성"
          subtitle="새로운 교환권을 생성하세요"
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          projectDetail={{ name: projectDetail.name, logo: projectDetail.logo }}
        />

        {/* Create Voucher Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/vouchers')}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                교환권 목록으로
              </button>
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">오류가 발생했습니다</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{submitError}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {/* 교환권 미리보기 및 안내사항 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div className="flex items-center mb-4">
                  <Gift className="w-5 h-5 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">교환권 미리보기</h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 미리보기 */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">미리보기</h3>
                    <VoucherPreviewCard
                      title={formData.name}
                      benefitText={formatVoucherPreviewBenefit(formData)}
                      validityText={formatVoucherPreviewValidity(formData)}
                      imageUrl={imagePreview || null}
                      isUploading={isImageUploading}
                      error={imageError}
                      onValidatedImage={handleValidatedImage}
                      onRemoveImage={handleRemoveImage}
                      onImageError={setImageError}
                    />
                  </div>

                  {/* 안내사항 */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">교환권 생성 도움말</h3>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-start">
                        <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                        <div>
          <ul className="text-sm text-blue-800 space-y-1">
                            <li>• 교환권 코드는 고유해야 합니다</li>
                            {/* <li>• 판매 금액·잔액은 0원으로 전송됩니다</li> */}
                            <li>• 이벤트 교환권은 멤버 제한 설정이 필요합니다</li>
                            <li>• 유효기간은 기간 또는 지정일로 설정 가능합니다</li>
                            <li>• 할인 정보는 쿠폰·이벤트 타입에 적용됩니다</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 기본 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Info className="w-5 h-5 mr-2" />
                  기본 정보
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      브랜드
                    </label>
                    <select
                      name="brandCode"
                      value={formData.brandCode || ''}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.brandCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      disabled={isLoadingBrands}
                    >
                      <option value="">브랜드를 선택하세요</option>
                      {brands.map((brand) => {
                        const nbsp = '\u00A0';
                        const indent = (nbsp + nbsp).repeat(brand.depth);
                        const prefix = brand.depth > 0 ? 'ㄴ ' : '';
                        return (
                          <option key={brand.code} value={brand.code}>
                            {`${indent}${prefix}${brand.name} (${brand.code})`}
                          </option>
                        );
                      })}
                    </select>
                    {errors.brandCode && (
                      <p className="mt-1 text-sm text-red-600">{errors.brandCode}</p>
                    )}
                  </div>

                  {/* 카테고리: 추가 시에는 목록에서 편집 안내 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      카테고리
                    </label>
                    <div className="flex items-center min-h-[2.5rem]">
                      <p className="text-sm text-gray-500">
                        카테고리/그룹 목록 &gt; 교환권 관리 에서 편집이 가능합니다.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      교환권 코드 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="masterCode"
                      value={formData.masterCode}
                      onChange={handleInputChange}
                      maxLength={20}
                      title="영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 입력 가능"
                      className={`w-full px-3 py-2 border ${errors.masterCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="영문, 숫자, _, - 만 입력"
                    />
                    {errors.masterCode && (
                      <p className="mt-1 text-sm text-red-600">{errors.masterCode}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상품 기준 코드
                    </label>
                    <input
                      type="text"
                      name="productCode"
                      value={formData.productCode}
                      onChange={handleInputChange}
                      maxLength={20}
                      className={`w-full px-3 py-2 border ${errors.productCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="상품 기준 코드를 입력하세요"
                    />
                    {errors.productCode && (
                      <p className="mt-1 text-sm text-red-600">{errors.productCode}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      교환권 상태 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="ACTIVE">활성</option>
                      <option value="INACTIVE">비활성</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      교환권 타입 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="PREPAID" hidden>
                        선불권
                      </option>
                      <option value="COUPON">쿠폰</option>
                      <option value="BIZ" hidden>
                        기업용
                      </option>
                      <option value="EVENT">이벤트</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      교환권 명칭 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={20}
                      className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="교환권 명칭을 입력하세요"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 가격 정보 — 당분간 숨김 (기본값 price/balance 0 유지) */}
              <div className="hidden bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  가격 정보
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      판매 금액 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.price ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0"
                    />
                    {errors.price && (
                      <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      사용 가능 잔액 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="balance"
                      value={formData.balance}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.balance ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0"
                    />
                    {errors.balance && (
                      <p className="mt-1 text-sm text-red-600">{errors.balance}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 할인 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Percent className="w-5 h-5 mr-2" />
                  할인 정보
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      할인 대상 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="discountTarget"
                      value={formData.discountTarget}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="PRODUCT">상품</option>
                      <option value="PAYMENT">결제</option>
                      <option value="REWARD">리워드</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      할인 방식 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="discountType"
                      value={formData.discountType}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="AMOUNT">정액</option>
                      <option value="PERCENTAGE">정률</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      할인 금액 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="discountValue"
                      value={formData.discountValue}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.discountValue ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0"
                    />
                    {errors.discountValue && (
                      <p className="mt-1 text-sm text-red-600">{errors.discountValue}</p>
                    )}
                  </div>

                  {formData.discountType === 'PERCENTAGE' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        최대 할인 금액
                      </label>
                      <input
                        type="number"
                        name="maxDiscount"
                        value={formData.maxDiscount}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.maxDiscount ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="0 (제한없음)"
                      />
                      {errors.maxDiscount && (
                        <p className="mt-1 text-sm text-red-600">{errors.maxDiscount}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      최소 결제 금액
                    </label>
                    <input
                      type="number"
                      name="minPayment"
                      value={formData.minPayment}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.minPayment ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0 (제한없음)"
                    />
                    {errors.minPayment && (
                      <p className="mt-1 text-sm text-red-600">{errors.minPayment}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 유효기간 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  유효기간 정보
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      유효기간 타입 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="validType"
                      value={formData.validType}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={0}>기간</option>
                      <option value={1}>지정일</option>
                    </select>
                  </div>

                  {Number(formData.validType) === 0 ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        유효기간(일) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="validPeriod"
                        value={formData.validPeriod}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.validPeriod ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="30"
                      />
                      {errors.validPeriod && (
                        <p className="mt-1 text-sm text-red-600">{errors.validPeriod}</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        유효 일시 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        name="validUntil"
                        step={1}
                        value={formData.validUntil}
                        onChange={handleInputChange}
                        className={`w-full min-w-[200px] px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.validUntil ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.validUntil && (
                        <p className="mt-1 text-sm text-red-600">{errors.validUntil}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 제한 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  제한 정보
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      발행 제한 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="issueLimit"
                      value={formData.issueLimit}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.issueLimit ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0 (제한없음)"
                    />
                    {errors.issueLimit && (
                      <p className="mt-1 text-sm text-red-600">{errors.issueLimit}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      멤버 제한
                    </label>
                    <input
                      type="number"
                      name="memberLimit"
                      value={formData.memberLimit}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.memberLimit ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0 (제한없음)"
                    />
                    {errors.memberLimit && (
                      <p className="mt-1 text-sm text-red-600">{errors.memberLimit}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 추가 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  추가 정보
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      짧은 소개
                    </label>
                    <input
                      type="text"
                      name="comment"
                      value={formData.comment}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.comment ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="교환권에 대한 짧은 소개를 입력하세요"
                      maxLength={50}
                    />
                    {errors.comment && (
                      <p className="mt-1 text-sm text-red-600">{errors.comment}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      교환권 상세
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="교환권에 대한 상세 설명을 입력하세요"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      교환권 유의사항
                    </label>
                    <textarea
                      name="notice"
                      value={formData.notice}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="교환권 사용 시 유의사항을 입력하세요"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  초기화
                </button>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => navigate('/vouchers')}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || isImageUploading}
                    className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center ${
                      isSubmitting || isImageUploading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {isSubmitting ? '생성 중...' : '교환권 생성'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default VoucherCreate;